import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, BookOpen, Trophy, CheckCircle2, Circle, Lock, ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { useStudyTrail, StudyModule } from "@/hooks/useStudyTrail";
import { useIsAdmin } from "@/hooks/useUserRole";
import { ModuleContent } from "./ModuleContent";
import { cn } from "@/lib/utils";

interface StudyTrailViewProps {
  category: string;
  categoryTitle: string;
  onBack: () => void;
  isPremium?: boolean;
  isTrialActive?: boolean;
  onUpgrade?: () => void;
}

const categoryIcons: Record<string, string> = {
  power_bi: "📊",
  sql: "🗄️",
  python: "🐍",
};

interface SubStep {
  type: "intro" | "quiz" | "exam";
  title: string;
  index: number;
}

export function StudyTrailView({ category, categoryTitle, onBack, isPremium, isTrialActive, onUpgrade }: StudyTrailViewProps) {
  const { modules, isLoading, updateProgress, completedCount, totalCount, trailProgress } = useStudyTrail(category, isPremium, isTrialActive);
  const { isAdmin } = useIsAdmin();
  const [activeModule, setActiveModule] = useState<StudyModule | null>(null);
  const [selectedStep, setSelectedStep] = useState<number | undefined>(undefined);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

  // Fetch videos & quizzes for the expanded module to build sub-steps in sidebar
  const { data: expandedQuizzes = [] } = useQuery({
    queryKey: ["sidebar-quizzes", expandedModuleId],
    queryFn: async () => {
      const { data } = await supabase
        .from("quizzes")
        .select("id, question")
        .eq("module_id", expandedModuleId!)
        .eq("is_active", true);
      return data || [];
    },
    enabled: !!expandedModuleId,
  });

  const expandedModule = modules.find((m) => m.id === expandedModuleId);

  // Sub-steps for expanded module: Aula → 3 questões → Prova final
  const subSteps: SubStep[] = (() => {
    if (!expandedModuleId) return [];
    const result: SubStep[] = [];
    const questionsBeforeExam = 3;

    if (expandedModule?.lesson_content) {
      result.push({ type: "intro", title: "Aula", index: result.length });
    }

    const practice = expandedQuizzes.slice(0, questionsBeforeExam);
    practice.forEach((_q, i) => {
      result.push({ type: "quiz", title: `Exercício ${i + 1}`, index: result.length });
    });

    if (expandedQuizzes.length > questionsBeforeExam) {
      result.push({ type: "exam", title: "Prova do Módulo", index: result.length });
    }

    return result;
  })();

  const handleOpenModule = (module: StudyModule) => {
    if (!isAdmin && module.progress_status === "locked") return;

    if (module.progress_status !== "completed") {
      updateProgress.mutate({ moduleId: module.id, status: "in_progress" });
    }
    setActiveModule(module);
    setSelectedStep(undefined);
    setExpandedModuleId(module.id);
  };

  const handleToggleExpand = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedModuleId(prev => prev === moduleId ? null : moduleId);
  };

  const handleStepSelect = (stepIndex: number) => {
    setSelectedStep(stepIndex);
  };

  const handleCompleteModule = (accuracy: number) => {
    if (activeModule) {
      updateProgress.mutate({
        moduleId: activeModule.id,
        status: "completed",
        quizAccuracy: accuracy,
      });
      setActiveModule(null);
      setExpandedModuleId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Skeleton className="h-24 flex-1 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Trilha de {categoryTitle} em construção
          </h3>
          <p className="text-muted-foreground max-w-md">
            Estamos preparando os módulos desta trilha. Em breve você terá uma jornada completa de aprendizado!
          </p>
        </div>
      </div>
    );
  }

  const stepIcon = (type: string) => {
    if (type === "intro") return <BookOpen className="h-3 w-3" />;
    if (type === "quiz") return <HelpCircle className="h-3 w-3" />;
    return <CheckCircle2 className="h-3 w-3" />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-3xl">{categoryIcons[category] || "📚"}</span>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">
            Trilha de {categoryTitle}
          </h2>
          <p className="text-sm text-muted-foreground">
            {totalCount} módulos • {completedCount} concluídos
          </p>
        </div>
        {trailProgress === 100 && (
          <Badge className="bg-green-500 text-white gap-1">
            <Trophy className="h-3 w-3" /> Completa
          </Badge>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Progresso da trilha</span>
          <span className="font-semibold">{trailProgress}%</span>
        </div>
        <Progress value={trailProgress} className="h-3" />
      </div>

      {/* Side-by-side layout */}
      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        {/* Left panel — module sidebar */}
        <div className="w-full lg:w-[340px] shrink-0">
          <div className="rounded-lg border bg-card p-3 space-y-1 sticky top-4">
            <h3 className="text-sm font-semibold text-muted-foreground px-2 pb-2">
              📋 Módulos ({totalCount})
            </h3>
            {modules.map((module, index) => {
              const isLocked = module.progress_status === "locked";
              const isCompleted = module.progress_status === "completed";
              const isActive = activeModule?.id === module.id;
              const isExpanded = expandedModuleId === module.id;
              const hasSubSteps = isExpanded && subSteps.length > 0;

              return (
                <div key={module.id}>
                  <button
                    className={cn(
                      "w-full flex items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors",
                      "hover:bg-muted/50",
                      isActive && "bg-primary/10 text-primary font-medium",
                      isLocked && "opacity-50",
                      !module.is_active && isAdmin && "opacity-60 border border-dashed border-muted-foreground/30"
                    )}
                    onClick={() => handleOpenModule(module)}
                    disabled={isLocked && !isAdmin}
                  >
                    {/* Expand chevron */}
                    <span
                      className="shrink-0 cursor-pointer"
                      onClick={(e) => handleToggleExpand(module.id, e)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </span>

                    {/* Status icon */}
                    <span className="shrink-0">
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : isLocked ? (
                        <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </span>

                    {/* Title */}
                    <span className={cn(
                      "flex-1 truncate",
                      isCompleted && "text-muted-foreground line-through",
                      isLocked && "text-muted-foreground"
                    )}>
                      {index + 1}. {module.title}
                    </span>

                    {/* Badges */}
                    {!module.is_active && isAdmin && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 bg-muted text-muted-foreground border-muted-foreground/30 shrink-0">
                        Rascunho
                      </Badge>
                    )}
                    {module.is_free && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 bg-emerald-500/10 text-emerald-600 border-emerald-500/20 shrink-0">
                        Free
                      </Badge>
                    )}
                    {!module.is_free && isLocked && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-500/10 text-amber-700 border-amber-500/20 shrink-0">
                        PRO
                      </Badge>
                    )}
                  </button>

                  {/* Sub-steps dropdown */}
                  {hasSubSteps && (
                    <div className="ml-8 mt-1 mb-1 space-y-0.5 border-l-2 border-muted pl-3">
                      {subSteps.map((sub, sIdx) => (
                        <button
                          key={sIdx}
                          className={cn(
                            "w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors",
                            "hover:bg-muted/50 text-muted-foreground",
                            selectedStep === sIdx && isActive && "bg-primary/10 text-primary font-medium"
                          )}
                          onClick={() => {
                            if (!isActive) handleOpenModule(module);
                            handleStepSelect(sIdx);
                          }}
                        >
                          {stepIcon(sub.type)}
                          <span className="truncate">{sub.title}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Expanded but no sub-steps */}
                  {isExpanded && subSteps.length === 0 && (
                    <div className="ml-8 mt-1 mb-1 pl-3 border-l-2 border-muted">
                      <p className="text-xs text-muted-foreground py-1">Nenhuma aula configurada</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel — module content */}
        <div className="flex-1 min-w-0">
          {activeModule ? (
            <ModuleContent
              module={activeModule}
              onBack={() => setActiveModule(null)}
              onComplete={handleCompleteModule}
              inline
              initialStep={selectedStep}
            />
          ) : (
            <div className="rounded-lg border bg-card flex flex-col items-center justify-center py-16 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">
                Selecione um módulo ao lado para começar
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
